import { z } from "zod";

const AGENT_COORDINATOR_NAME_KEYS = {
  type: "type",
  projectId: "projectId",
  actorId: "actorId",
  devAlias: "devAlias",
  initialSnapshotRef: "initialSnapshotRef",
} as const;

const agentCoordinatorNameSchema = z.object({
  type: z.literal("project"),
  projectId: z.string().min(1),
  // Every Durable Object instance is keyed to the user acting through it; a name
  // with no actorId is a routing bug, so reject it at the parse boundary.
  actorId: z.string().min(1),
  devAlias: z.string().min(1).optional(),
  initialSnapshotRef: z.string().min(1).optional(),
});

export type AgentCoordinatorName = z.infer<typeof agentCoordinatorNameSchema>;

export function createAgentCoordinatorName({
  projectId,
  actorId,
  devAlias,
  initialSnapshotRef,
}: {
  projectId: string;
  actorId: string;
  devAlias?: string | null;
  initialSnapshotRef?: string | null;
}): string {
  const params = new URLSearchParams();
  params.set(AGENT_COORDINATOR_NAME_KEYS.type, "project");
  params.set(AGENT_COORDINATOR_NAME_KEYS.projectId, projectId);
  params.set(AGENT_COORDINATOR_NAME_KEYS.actorId, actorId);
  if (devAlias) {
    params.set(AGENT_COORDINATOR_NAME_KEYS.devAlias, devAlias);
  }
  if (initialSnapshotRef) {
    params.set(
      AGENT_COORDINATOR_NAME_KEYS.initialSnapshotRef,
      initialSnapshotRef,
    );
  }
  // Percent-encode the whole query string so the name is URL-safe and *is* the
  // Durable Object key: `/agents/replo-agent/<name>` carries it verbatim (no
  // re-encode at the URL boundary) and `idFromName(name)` matches everywhere.
  return encodeURIComponent(params.toString());
}

export function parseAgentCoordinatorName(name: string): AgentCoordinatorName {
  return agentCoordinatorNameSchema.parse(getAgentCoordinatorNameParams(name));
}

function getAgentCoordinatorNameParams(name: string): unknown {
  // The name is the percent-encoded query string `createAgentCoordinatorName`
  // emits; decode once to recover the `key=value&...` form before reading it.
  const params = new URLSearchParams(decodeURIComponent(name));
  return {
    type: params.get(AGENT_COORDINATOR_NAME_KEYS.type),
    projectId: params.get(AGENT_COORDINATOR_NAME_KEYS.projectId),
    actorId: params.get(AGENT_COORDINATOR_NAME_KEYS.actorId) ?? undefined,
    devAlias: params.get(AGENT_COORDINATOR_NAME_KEYS.devAlias) ?? undefined,
    initialSnapshotRef:
      params.get(AGENT_COORDINATOR_NAME_KEYS.initialSnapshotRef) ?? undefined,
  };
}
